import dayjs from "dayjs";
import Loading from 'quasar/src/plugins/loading/Loading.js';
import { computed, defineComponent, reactive, ref, watch } from "vue";
import { AttachedFile, Taikyo } from "~/api/data/TaikyoNotificationEntity";
import { MsgBox } from "~/components/MsgBox";
import { Log } from "~/extensions/utils";
import { createAddress } from "~/extensions/utils/AddressHelper";
import { KeyReturnAdrKb, KeyReturnNameKb, RenrakuKb, TaikyoRiyuKb } from "~/extensions/utils/DefineTypes";
import { validateSelectedFiles } from "~/pages/taikyo/notification/Models/TaikyoNotificationFileHelper";
import { clearContactFields, clearReturnAddressFields, updateKeyReturnDateTime } from "~/pages/taikyo/notification/Models/TaikyoFormHelper";
import { store } from "~/pages/taikyo/store/TaikyoStore";
import { myPageStore } from "~/store/MyPageStore";
import { TaikyoValidator } from "~/pages/taikyo/notification/Models/TaikyoNotificationInputValidator";
import { TaikyoNotificationViewModel } from "~/pages/taikyo/notification/Models/TaikyoNotificationViewModel";
import { updateKaiyakuYmd } from "../Models/TaikyoFormHelper";
const log = Log.create("TaikyoNotificationList");
export default defineComponent({
    name: "taikyoNotificationList",
    emit: ["remove"],
    props: {
        row: {
            type: Taikyo,
            required: true
        },
        columns: {
            type: Array,
            required: true
        },
        taikyoForInput: {
            type: Object,
            required: true
        },
        desiredDatePrecautions: {
            type: Object,
            required: true
        }
    },
    setup(props, { expose, emit }) {
        // 表示条件と入力値の検証
        const isMultipleKeiyaku = computed(() => store.taikyoNotificationEntity.taikyoList.filter(x => !x.isDeleted).length > 1);
        const isMMA = myPageStore.personal.isMma;
        const viewModel = new TaikyoNotificationViewModel(store.taikyoNotificationEntity, props.taikyoForInput);
        const validators = reactive(new Map());
        const getValidator = (row) => {
            if (!validators.has(row.taikyoNo)) {
                validators.set(row.taikyoNo, new TaikyoValidator(viewModel, row, props.taikyoForInput));
            }
            return validators.get(row.taikyoNo);
        };
        // 解約理由
        const clearTaikyoRiyu = (taikyo) => {
            if (viewModel.isDisableTaikyoRiyu(taikyo)) {
                taikyo.taikyoRiyu = null;
            }
        };
        // 連絡先、鍵返却日時、退去立会
        const clearRenrakuField = clearContactFields;
        const applyDateTime = updateKeyReturnDateTime;
        // 希望日時の注意事項
        const desiredDateMessage = computed(() => props.desiredDatePrecautions.precaution);
        const desiredDateEntryMessage = computed(() => props.desiredDatePrecautions.entryPrecaution);
        // 鍵返却先と住所
        const clearSoufuField = (row) => clearReturnAddressFields(row, myPageStore.personal);
        const findAddress = async (row) => {
            if (getValidator(row)?.data.error && getValidator(row)?.data.errorFields.includes("postCode")) {
                return;
            }
            if (row.postCode?.length === 7) {
                row.postCode = row.postCode.substring(0, 3) + "-" + row.postCode.substring(3);
            }
            try {
                if (/^\d{3}-\d{4}$/.test(row.postCode)) {
                    row.adr1 = await createAddress(row.postCode);
                }
            }
            catch (ex) {
                if (ex?.status !== 404) {
                    log.error("findAddress", ex);
                }
            }
        };
        // 添付ファイル
        const dialogOpenHason = ref(false);
        const dialogOpenOther = ref(false);
        const tmpFile = ref([]);
        const errorFlg = ref(false);
        const openDialogHason = () => {
            dialogOpenHason.value = true;
        };
        const openDialogOther = () => {
            dialogOpenOther.value = true;
        };
        // ファイル操作
        const fileDelete = (file) => {
            file.delFlg = true;
        };
        const onFilePicked = async (value, row) => {
            errorFlg.value = false;
            const files = Array.isArray(value) ? value : [value];
            const msg = validateSelectedFiles(files, row);
            errorFlg.value = msg !== "";
            if (msg !== "") {
                await MsgBox.error(msg);
            }
        };
        async function uploadFile(files, row, fileKb) {
            const fileList = [];
            try {
                Loading.show();
                const currentFiles = Array.isArray(files) ? files : [files];
                currentFiles.forEach((file, index) => {
                    const attached = new AttachedFile();
                    const now = new Date();
                    const date = String(now.getFullYear()) +
                        String(now.getMonth() + 1).padStart(2, "0") +
                        String(now.getDate()).padStart(2, "0") +
                        String(now.getHours()).padStart(2, "0") +
                        String(now.getMinutes()).padStart(2, "0") +
                        String(now.getSeconds()).padStart(2, "0");
                    attached.fileId = String(row.taikyoNo) + date + String(index);
                    attached.fileKb = fileKb;
                    attached.fileName = file.name;
                    attached.newFlg = true;
                    row.fileList.push(attached);
                    fileList.push(attached);
                });
                row.files = Array.from(currentFiles);
                tmpFile.value = [];
                await store.insertFile(row.taikyoNo, currentFiles, fileList);
            }
            catch (ex) {
                row.fileList
                    .filter(x => fileList.some(y => y.fileId === x.fileId))
                    .forEach(x => {
                    x.delFlg = true;
                });
                log.error("setup", ex);
                MsgBox.error(ex.message);
            }
            finally {
                Loading.hide();
                dialogOpenHason.value = false;
                dialogOpenOther.value = false;
            }
        }
        const remove = (orgNyukyokeiyakuNo) => {
            emit("remove", orgNyukyokeiyakuNo);
        };
        function runValid() {
            let hasError = false;
            validators.forEach(v => {
                if (v.error) {
                    hasError = true;
                }
            });
            return hasError;
        }
        watch(() => props.row.taikyoYmd, () => {
            updateKaiyakuYmd(props.row);
        });
        expose({
            runValid
        });
        return {
            props,
            isMultipleKeiyaku,
            dayjs,
            isMMA,
            remove,
            openDialogHason,
            dialogOpenHason,
            openDialogOther,
            dialogOpenOther,
            uploadFile,
            fileDelete,
            tmpFile,
            getValidator,
            clearSoufuField,
            clearRenrakuField,
            clearTaikyoRiyu,
            findAddress,
            TaikyoRiyuKb,
            RenrakuKb,
            KeyReturnNameKb,
            KeyReturnAdrKb,
            onFilePicked,
            errorFlg,
            applyDateTime,
            desiredDateMessage,
            desiredDateEntryMessage
        };
    }
});
